import React, { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Footer,
  SEO,
  breadcrumbSchema,
  faqSchema,
} from '@coupling/ui';
import ContactSalesModal from '../components/ContactSalesModal';
import { useBooking } from '../components/BookingProvider';
import Header from '../components/Header';
import StripePaymentsSection from '../components/hardware/StripePaymentsSection';
import PricingFactsSection from '../components/marketing/PricingFactsSection';
import { seoConfig } from '../config/seo';
import {
  bonusVoucherCopy,
  freeForUsersFact,
  monthlyPrice,
  primaryDemoCta,
  productSummary,
  tryCouplingCta,
} from '../content/siteMessaging';

const merchantQuestions = [
  {
    question: 'Who pays for Coupling?',
    answer:
      `Merchant access is ${monthlyPrice}, with no monthly subscription dues. ${freeForUsersFact}. Token minting costs, ordinary card-processor fees, and optional paid merchant memberships or offers may still apply.`,
  },
  {
    question: 'Are there transaction fees on redemptions?',
    answer:
      'No Coupling fee on each sale. Card networks still take their cut when you take cards. You can budget the program once instead of taking a Coupling fee on each redemption.',
  },
  {
    question: 'What does it cost to issue tokens?',
    answer:
      'Token minting has a separate cost based on the amount issued. Coupling shows the estimate before you create a token and the exact amount before you confirm payment.',
  },
];

export const Pricing: React.FC = () => {
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const booking = useBooking();

  return (
    <div className="min-h-screen bg-[#fffaf4]">
      <SEO
        title={`Pricing for WNC merchants - ${seoConfig.siteName}`}
        description="Free Coupling access for Western North Carolina merchants and users, with no monthly subscription dues. Token minting and ordinary card-processor costs may still apply."
        canonical={`${seoConfig.siteUrl}/pricing`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/pricing`}
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', url: seoConfig.siteUrl },
            { name: 'Pricing', url: `${seoConfig.siteUrl}/pricing` },
          ]),
          faqSchema(
            merchantQuestions.map(({ question, answer }) => ({ question, answer })),
          ),
        ]}
      />
      <Header />

      <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,236,214,0.55),_transparent_32%),linear-gradient(135deg,#c85c2f_0%,#8f3d1b_52%,#5f2a19_100%)] px-4 pb-20 pt-28 sm:pt-32">
        <div
          className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(to_top,rgba(255,250,244,0.98),rgba(255,250,244,0))]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="text-white">
              <Badge variant="secondary" size="md" className="border border-white/20 bg-white/10 text-white">
                WNC merchant pricing
              </Badge>
              <h1 className="mt-6 max-w-3xl text-5xl font-bold tracking-tight text-white sm:text-6xl">
                Clear pricing for the shop tools you can run tomorrow
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/88 sm:text-xl">
                {productSummary} Merchant access is free with no monthly
                subscription dues. The pricing facts below explain token
                minting and ordinary payment-processing costs.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => booking.open('demo')}
                >
                  {primaryDemoCta}
                </Button>
                <button
                  type="button"
                  onClick={() => setIsSalesOpen(true)}
                  className="inline-flex items-center justify-center rounded-full border border-white/45 bg-white/10 px-7 py-3 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/18"
                >
                  {tryCouplingCta}
                </button>
              </div>

              <p className="mt-5 text-sm font-medium text-white/85">
                {bonusVoucherCopy}
              </p>
            </div>

            <Card className="border border-white/35 bg-white/95 shadow-[0_28px_80px_rgba(31,15,9,0.18)]">
              <div className="p-8 sm:p-10">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-primary">
                  Merchant plan
                </p>
                <div className="mt-4 text-5xl font-bold tracking-tight text-neutral-900">
                  {monthlyPrice}
                </div>
                <p className="mt-4 text-base leading-relaxed text-neutral-600">
                  Merchant access includes checkout, appointments, and rewards
                  with no monthly subscription dues. Token minting and ordinary
                  card-processor costs are disclosed separately.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <PricingFactsSection />

      <StripePaymentsSection />

      <section className="bg-[#f8efe6] px-4 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold text-brand-primary shadow-sm">
              Merchant questions
            </span>
            <h2 className="mt-6 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
              Common pricing questions from WNC merchants
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-neutral-600">
              Get the practical answers on who pays, what fees exist, and how
              token minting and processor costs work before you decide whether to book a demo.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {merchantQuestions.map(({ question, answer }) => (
              <Card
                key={question}
                className="border border-brand-primary/10 bg-white shadow-[0_20px_48px_rgba(130,64,29,0.1)]"
              >
                <div className="p-7">
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {question}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                    {answer}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-neutral-900 px-4 py-24 text-white">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold tracking-tight sm:text-5xl">
            See whether Coupling fits your shop before you commit
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/80">
            {primaryDemoCta} to walk through your current POS workflow, or contact us
            if you want to talk through rollout details for your WNC location.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => booking.open('demo')}
            >
              {primaryDemoCta}
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="bg-white/10 text-white hover:bg-white/20"
              onClick={() => setIsSalesOpen(true)}
            >
              {tryCouplingCta}
            </Button>
          </div>
        </div>
      </section>

      <ContactSalesModal
        isOpen={isSalesOpen}
        onClose={() => setIsSalesOpen(false)}
      />

      <Footer />
    </div>
  );
};

export default Pricing;
